import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { colors } from '../../theme/colorTokens';

const ProductLogo = ({ product, size = 32 }) => {
  if (product.logoType === 'image') {
    return (
      <Box
        component="img"
        src={product.logoSrc}
        alt={product.name}
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: product.logoPad ? 'contain' : 'cover',
          flexShrink: 0,
          border: `1px solid ${colors.border}`,
          bgcolor: product.logoBg || colors.bgCard,
          p: product.logoPad ? `${product.logoPad}px` : 0,
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: product.logoBg || colors.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: size * 0.45,
        fontWeight: 700,
        color: colors.bgCard,
        border: `1px solid ${colors.border}`,
      }}
    >
      {product.logoInitial}
    </Box>
  );
};

const getScoreColor = (score) => {
  if (score >= 85) return colors.success;
  if (score >= 75) return colors.primary;
  return colors.textSecondary;
};

const ComparisonHub = () => {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const [activeFilter, setActiveFilter] = useState(t('home.comparisonHub.filterAll'));
  const [showScoreInfo, setShowScoreInfo] = useState(false);
  // Hydration-safe: default false matches Puppeteer prerender at 1024px.
  // useEffect updates to live value after hydration.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 899px)');
    setIsMobile(mql.matches);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const filterChips = [
    t('home.comparisonHub.filterAll'),
    t('home.comparisonHub.filterNoFee'),
    t('home.comparisonHub.filterCashback'),
    t('home.comparisonHub.filterNewcomers'),
  ];

  const comparisonProducts = [
    {
      name: 'Neo Financial Mastercard',
      logoType: 'image',
      logoSrc: '/logos/neo-icon.webp',
      score: 87,
      recommended: true,
      fees: '$0',
      eligibility: t('home.comparisonHub.product1Eligibility'),
      rewards: t('home.comparisonHub.product1Rewards'),
      newcomerFit: t('home.comparisonHub.fitExcellent'),
      badges: [t('home.comparisonHub.filterNoFee'), t('home.comparisonHub.filterCashback')],
    },
    {
      name: 'Tangerine Money-Back',
      logoType: 'image',
      logoSrc: '/logos/tangerine-icon.webp',
      score: 84,
      recommended: false,
      fees: '$0',
      eligibility: t('home.comparisonHub.product2Eligibility'),
      rewards: t('home.comparisonHub.product2Rewards'),
      newcomerFit: t('home.comparisonHub.fitGood'),
      badges: [t('home.comparisonHub.filterNoFee'), t('home.comparisonHub.filterCashback')],
    },
    {
      name: 'Scotiabank StartRight',
      logoType: 'image',
      logoSrc: '/logos/scotiabank-icon.webp',
      score: 81,
      recommended: false,
      fees: t('home.comparisonHub.product3Fees'),
      eligibility: t('home.comparisonHub.product3Eligibility'),
      rewards: t('home.comparisonHub.product3Rewards'),
      newcomerFit: t('home.comparisonHub.fitExcellent'),
      badges: [t('home.comparisonHub.filterNewcomers')],
    },
  ];

  const topPick = {
    name: 'Neo Financial Mastercard',
    logoType: 'image',
    logoSrc: '/logos/neo-icon.webp',
    score: 87,
    metrics: [
      t('home.comparisonHub.filterNoFee'),
      t('home.comparisonHub.topPickMetric2'),
      t('home.comparisonHub.topPickMetric3'),
    ],
    primaryCta: t('home.comparisonHub.viewOffer'),
    secondaryCta: t('home.comparisonHub.details'),
    to: 'creditCards',
  };

  const fitExcellent = t('home.comparisonHub.fitExcellent');

  return (
    <Box
      sx={{
        backgroundColor: '#FAFBFD',
        pt: { xs: 6, md: 8 },
        pb: { xs: 6, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        {/* Section header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            mb: 4,
            gap: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 1.5,
              }}
            >
              {t('home.comparisonHub.overline')}
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 600, color: colors.textPrimary, mb: 1.5 }}
            >
              {t('home.comparisonHub.title')}
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                color: colors.textPrimary,
                lineHeight: 1.5,
                maxWidth: '50ch',
              }}
            >
              {t('home.comparisonHub.subtitle')}
            </Typography>
          </Box>

          {/* Advertising disclosure link - Layer 2 of 3-layer disclosure */}
          <Typography
            component={RouterLink}
            to={lp('howWeMonetize')}
            sx={{
              fontSize: '12px',
              color: colors.textSecondary,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('home.comparisonHub.adDisclosure')}
          </Typography>
        </Box>

        {/* Editorial intro — contextual bridge */}
        <Typography sx={{ fontSize: 16, color: colors.textPrimary, fontWeight: 500, maxWidth: 700, mx: 'auto', textAlign: 'center', mb: 2, lineHeight: 1.6 }}>
          {t('home.comparisonHub.editorialIntro')}
        </Typography>

        {/* FinMaple Score explainer toggle */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            component="span"
            onClick={() => setShowScoreInfo(!showScoreInfo)}
            sx={{
              fontSize: '14px',
              color: colors.primary,
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {showScoreInfo ? t('home.comparisonHub.hideDetail') : t('home.comparisonHub.whatIsScore')}
          </Typography>
          {showScoreInfo && (
            <Typography
              sx={{
                fontSize: '14px',
                color: colors.textSecondary,
                maxWidth: 600,
                mx: 'auto',
                mt: 1.5,
                lineHeight: 1.6,
                backgroundColor: colors.bgSoft,
                borderRadius: '10px',
                p: 2,
                border: `1px solid ${colors.border}`,
              }}
            >
              {t('home.comparisonHub.scoreExplainer')}
            </Typography>
          )}
        </Box>

        {/* Filter chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {filterChips.map((chip) => (
            <Chip
              key={chip}
              label={chip}
              clickable
              onClick={() => setActiveFilter(chip)}
              sx={{
                height: '32px',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '999px',
                ...(activeFilter === chip
                  ? {
                      backgroundColor: colors.primary,
                      color: colors.bgCard,
                      '&:hover': { backgroundColor: colors.primaryHover },
                    }
                  : {
                      backgroundColor: colors.bgCard,
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`,
                      '&:hover': { backgroundColor: colors.border },
                    }),
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
          }}
        >
          {/* Left: Comparison table / mobile cards */}
          <Box sx={{ flex: { md: '1 1 66.67%' }, minWidth: 0 }}>
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {comparisonProducts.map((product) => (
                    <MobileProductCard key={product.name} product={product} t={t} lp={lp} fitExcellent={fitExcellent} />
                  ))}
                </Box>
              ) : (
              <TableContainer
                sx={{
                  backgroundColor: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '14px',
                  boxShadow: '0 1px 4px rgba(11,18,32,0.04)',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: colors.bgCard }}>
                      <TableCell sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: '14px', width: '160px' }}>
                        {t('home.comparisonHub.colProduct')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: '14px' }}>{t('home.comparisonHub.colFees')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: '14px' }}>{t('home.comparisonHub.colEligibility')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: '14px' }}>{t('home.comparisonHub.colRewards')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: '14px' }}>{t('home.comparisonHub.colNewcomers')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: '14px', textAlign: 'center' }}>Score</TableCell>
                      <TableCell sx={{ width: '130px' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisonProducts.map((product, i) => (
                      <TableRow
                        key={product.name}
                        sx={{
                          height: '56px',
                          transition: 'background-color 0.15s ease',
                          backgroundColor: product.recommended
                            ? '#FFF5F5'
                            : i % 2 === 1
                              ? colors.bgSoft
                              : colors.bgCard,
                          '&:hover': {
                            backgroundColor: product.recommended ? '#FFF1F0' : colors.bgSoft,
                            '& .cta-btn': { opacity: 1 },
                          },
                          ...(product.recommended && {
                            position: 'relative',
                            '& td:first-of-type': {
                              borderLeft: '3px solid rgba(214,31,44,0.6)',
                              borderTopLeftRadius: '14px',
                              borderBottomLeftRadius: '14px',
                            },
                          }),
                        }}
                      >
                        <TableCell sx={{ width: '200px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <ProductLogo product={product} size={32} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary, lineHeight: 1.3 }}>
                                {product.name}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {product.recommended && (
                                  <Chip
                                    label={t('home.comparisonHub.recommended')}
                                    size="small"
                                    sx={{
                                      height: '18px',
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      backgroundColor: '#FCECEC',
                                      color: colors.accent,
                                      borderRadius: '999px',
                                    }}
                                  />
                                )}
                                {product.newcomerFit === fitExcellent && (
                                  <Box component="span" sx={{ display: 'inline-block', bgcolor: colors.warningTint, color: colors.warningText, fontSize: 12, fontWeight: 600, px: 1.5, py: 0.25, borderRadius: 6 }}>
                                    {t('home.comparisonHub.idealNewcomers')}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '14px', color: colors.textPrimary }}>{product.fees}</TableCell>
                        <TableCell sx={{ fontSize: '14px', color: colors.textPrimary }}>{product.eligibility}</TableCell>
                        <TableCell sx={{ fontSize: '14px', color: colors.textPrimary }}>{product.rewards}</TableCell>
                        <TableCell sx={{ fontSize: '14px', color: colors.textPrimary }}>{product.newcomerFit}</TableCell>
                        <TableCell sx={{ textAlign: 'center', pr: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '24px',
                              fontWeight: 600,
                              color: getScoreColor(product.score),
                              fontFeatureSettings: "'tnum'",
                              transition: 'color 0.15s ease',
                            }}
                          >
                            {product.score}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ pl: 1 }}>
                          <Button
                            className="cta-btn"
                            component={RouterLink}
                            to={lp('creditCards')}
                            variant="contained"
                            size="small"
                            sx={{
                              height: '34px',
                              fontSize: '14px',
                              fontWeight: 500,
                              borderRadius: '10px',
                              px: 3,
                              whiteSpace: 'nowrap',
                              backgroundColor: colors.primary,
                              textTransform: 'none',
                              boxShadow: 'none',
                              opacity: 0.85,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: colors.primaryHover,
                                boxShadow: 'none',
                                opacity: 1,
                                transform: 'scale(1.03)',
                              },
                            }}
                          >
                            {t('home.comparisonHub.viewOffer')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
          </Box>

          {/* Right: Top Pick Module - sticky, 3-4 cols */}
          <Box
            sx={{
              flex: { md: '0 0 33.33%' },
              maxWidth: { md: '33.33%' },
              width: '100%',
              position: { md: 'sticky' },
              top: { md: '80px' },
              alignSelf: { md: 'flex-start' },
            }}
          >
            <Box
              sx={{
                backgroundColor: '#FFFBFA',
                border: '1px solid #F0E2E2',
                borderRadius: '14px',
                boxShadow: '0 1px 8px rgba(214,31,44,0.04)',
                p: 2.5,
              }}
            >
              <Chip
                label={t('home.comparisonHub.recommended')}
                size="small"
                sx={{
                  height: '24px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: '#FCECEC',
                  color: colors.accent,
                  borderRadius: '999px',
                  mb: 2,
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <ProductLogo product={topPick} size={40} />
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                  {topPick.name}
                </Typography>
              </Box>

              {/* FinMaple Score - large */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: '34px',
                    fontWeight: 600,
                    color: colors.primary,
                    lineHeight: 1,
                  }}
                >
                  {topPick.score}
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: '12px', color: colors.textSecondary }}>
                    FinMaple Score
                  </Typography>
                  <Typography
                    component={RouterLink}
                    to={lp('howWeMonetize')}
                    sx={{
                      fontSize: '12px',
                      color: colors.textSecondary,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {t('home.comparisonHub.howWeRate')}
                  </Typography>
                </Box>
              </Box>

              {/* Metric chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {topPick.metrics.map((m) => (
                  <Chip
                    key={m}
                    label={m}
                    size="small"
                    sx={{
                      height: '24px',
                      fontSize: '12px',
                      fontWeight: 400,
                      backgroundColor: colors.bgSoft,
                      color: colors.textPrimary,
                      border: 'none',
                      borderRadius: '999px',
                    }}
                  />
                ))}
              </Box>

              {/* CTAs */}
              <Button
                component={RouterLink}
                to={lp(topPick.to)}
                variant="contained"
                fullWidth
                sx={{
                  height: '40px',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '10px',
                  backgroundColor: colors.primary,
                  textTransform: 'none',
                  boxShadow: 'none',
                  mb: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': { backgroundColor: colors.primaryHover, boxShadow: 'none', transform: 'scale(1.03)' },
                }}
              >
                {topPick.primaryCta}
              </Button>

              <Button
                component={RouterLink}
                to={lp(topPick.to)}
                variant="text"
                fullWidth
                sx={{
                  height: '36px',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: colors.textPrimary,
                  textTransform: 'none',
                  '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
                }}
              >
                {topPick.secondaryCta}
              </Button>

              {/* Inline disclosure - Layer 1 of 3-layer disclosure */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, pt: 2, borderTop: `1px solid ${colors.border}` }}>
                <InfoIcon sx={{ fontSize: '12px', color: colors.textSecondary }} />
                <Typography sx={{ fontSize: '12px', color: colors.textSecondary }}>
                  {t('home.comparisonHub.compensationDisclosure')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// Mobile product card component
const MobileProductCard = ({ product, t, lp, fitExcellent }) => (
  <Box
    sx={{
      backgroundColor: product.recommended ? '#FFF5F5' : colors.bgCard,
      border: product.recommended ? '1px solid #F2DCDC' : `1px solid ${colors.border}`,
      borderLeft: product.recommended ? '3px solid rgba(214,31,44,0.6)' : `1px solid ${colors.border}`,
      borderRadius: '14px',
      p: '16px',
      boxShadow: '0 1px 4px rgba(11,18,32,0.04)',
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ProductLogo product={product} size={36} />
        <Box>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
          {product.name}
        </Typography>
        {product.recommended && (
          <Chip
            label={t('home.comparisonHub.recommended')}
            size="small"
            sx={{
              height: '18px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor: '#FCECEC',
              color: colors.accent,
              borderRadius: '999px',
              mt: 0.5,
            }}
          />
        )}
        {product.newcomerFit === fitExcellent && (
          <Box component="span" sx={{ display: 'inline-block', bgcolor: colors.warningTint, color: colors.warningText, fontSize: 12, fontWeight: 600, px: 1.5, py: 0.25, borderRadius: 6, mt: 0.5 }}>
            {t('home.comparisonHub.idealNewcomers')}
          </Box>
        )}
        </Box>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ fontSize: '22px', fontWeight: 600, color: getScoreColor(product.score), lineHeight: 1 }}>
          {product.score}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: colors.textSecondary }}>FinMaple Score</Typography>
      </Box>
    </Box>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
      {product.badges.map((badge) => (
        <Chip
          key={badge}
          label={badge}
          size="small"
          sx={{
            height: '22px',
            fontSize: '12px',
            backgroundColor: colors.bgSoft,
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: '999px',
          }}
        />
      ))}
    </Box>

    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2, fontSize: '12px' }}>
      <Box>
        <Typography sx={{ fontSize: '12px', color: colors.textSecondary, mb: 0.25 }}>{t('home.comparisonHub.colFees')}</Typography>
        <Typography sx={{ fontSize: '14px', color: colors.textPrimary }}>{product.fees}</Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: '12px', color: colors.textSecondary, mb: 0.25 }}>{t('home.comparisonHub.colRewards')}</Typography>
        <Typography sx={{ fontSize: '14px', color: colors.textPrimary }}>{product.rewards}</Typography>
      </Box>
    </Box>

    <Button
      component={RouterLink}
      to={lp('creditCards')}
      variant="contained"
      fullWidth
      sx={{
        height: '36px',
        fontSize: '14px',
        fontWeight: 500,
        borderRadius: '10px',
        backgroundColor: colors.primary,
        textTransform: 'none',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        '&:hover': { backgroundColor: colors.primaryHover, boxShadow: 'none', transform: 'scale(1.03)' },
      }}
    >
      {t('home.comparisonHub.viewOffer')}
    </Button>
  </Box>
);

export default ComparisonHub;
